import React, { useState, useEffect, useRef } from 'react';
import { FaLeaf, FaPlus, FaCheck, FaShoppingBag, FaBan, FaPause } from 'react-icons/fa';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { MENU_DATA, CATEGORY_META } from './menuData';
import './Menu.css';
import './MenuCart.css';

const Menu = () => {
  const [activeTab, setActiveTab] = useState('deals');
  const sectionRef = useRef(null);
  const tabsRef = useRef(null);
  const { addItem, removeItem, openCart, itemCount, items: cartItems } = useCart();
  const [menuOverrides, setMenuOverrides] = useState([]);
  const [storeSettings, setStoreSettings] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('in-view')),
      { threshold: 0.1 }
    );
    sectionRef.current?.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Fetch menu overrides and store settings
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [overridesRes, settingsRes] = await Promise.all([
          axios.get('http://localhost:5000/api/settings/menu-overrides'),
          axios.get('http://localhost:5000/api/settings'),
        ]);
        if (overridesRes.data.success) setMenuOverrides(overridesRes.data.data);
        if (settingsRes.data.success) setStoreSettings(settingsRes.data.data);
      } catch (err) {
        console.log('Settings not available yet');
      }
    };
    fetchData();
  }, []);

  const getOverride = (itemId) => menuOverrides.find(o => o.itemId === itemId);

  const handleAdd = (item) => {
    if (storeSettings?.isPaused) return;
    const override = getOverride(item._id);
    if (override && !override.available) return;
    
    const alreadyInCart = cartItems.some(c => c._id === item._id);
    if (alreadyInCart) removeItem(item._id);
    else {
      // Use overridden price if exists
      const displayPrice = override?.price ?? item.price;
      addItem({ ...item, price: displayPrice });
    }
  };

  const handleTabClick = (key) => {
    setActiveTab(key);
    const tabEl = document.getElementById(`menu-tab-${key}`);
    if (tabEl && tabsRef.current) {
      tabEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  };

  const activeItems = (MENU_DATA[activeTab]?.items || []).filter(item => {
    const override = getOverride(item._id);
    return !override || override.available !== false;
  });

  return (
    <section id="menu" className="menu-section section-padding" ref={sectionRef}>
      <div className="menu-bg" />
      <div className="container">

        <div className="section-header reveal">
          <span className="section-tag">Our Menu</span>
          <div className="gold-line"><div className="gold-diamond" /></div>
          <h2 className="section-title">Order Takeaway</h2>
          <p className="section-subtitle" style={{ marginTop: '16px' }}>
            Fresh, flame-grilled and made to order — choose your favourite and add to cart
          </p>
        </div>

        {/* Store Paused Banner */}
        {storeSettings?.isPaused && (
          <div className="menu-paused-banner reveal">
            <FaPause className="paused-icon" />
            <div>
              <strong>Currently Not Accepting Orders</strong>
              <p>{storeSettings.pauseMessage || 'Please try again later.'}</p>
            </div>
          </div>
        )}

        {/* Scrollable tabs */}
        <div className="menu-tabs-wrapper reveal">
          <div className="menu-tabs" ref={tabsRef}>
            {Object.entries(CATEGORY_META).map(([key, val]) => (
              <button
                key={key}
                id={`menu-tab-${key}`}
                className={`menu-tab ${activeTab === key ? 'active' : ''}`}
                onClick={() => handleTabClick(key)}
              >
                <span className="tab-label">{val.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Item count badge */}
        <div className="menu-tab-info reveal">
          <span className="tab-item-count">
            {activeItems.length} {activeItems.length === 1 ? 'item' : 'items'} in{' '}
            <strong>{CATEGORY_META[activeTab]?.label}</strong>
          </span>
        </div>

        <div className="menu-items-grid reveal">
          {activeItems.map((item, i) => {
            const isAdded = cartItems.some(c => c._id === item._id);
            const override = getOverride(item._id);
            const displayPrice = override?.price ?? item.price;
            const isPaused = storeSettings?.isPaused;

            return (
              <div className={`menu-item-card ${isPaused ? 'paused' : ''}`} key={item._id || i} style={{ animationDelay: `${i * 0.05}s` }}>
                <div className="menu-item-top">
                  <div className="menu-item-number">{String(i + 1).padStart(2, '0')}</div>
                  <div className="menu-item-info">
                    <h3 className="menu-item-name">{item.name}</h3>
                    <p className="menu-item-desc">{item.desc}</p>
                    <span className="menu-item-price">€{Number(displayPrice).toFixed(2)}</span>
                  </div>
                  <button
                    className={`add-to-cart-btn ${isAdded ? 'added' : ''} ${isPaused ? 'disabled' : ''}`}
                    onClick={() => handleAdd(item)}
                    aria-label={`Add ${item.name} to cart`}
                    id={`add-btn-${item._id || i}`}
                    title={isPaused ? 'Orders paused' : 'Add to order'}
                    disabled={isPaused}
                  >
                    {isAdded ? <FaCheck /> : <FaPlus />}
                  </button>
                </div>
                <div className="menu-item-line" />
              </div>
            );
          })}
        </div>

        <div className="menu-bottom-note reveal">
          <FaLeaf className="note-icon" />
          <span>All dishes prepared fresh daily · Please inform us of any dietary requirements or allergies</span>
          <FaLeaf className="note-icon" />
        </div>
      </div>

      {itemCount > 0 && !storeSettings?.isPaused && (
        <div className="menu-cart-float" id="menu-cart-float">
          <div className="mcf-inner">
            <span className="mcf-count">{itemCount} item{itemCount > 1 ? 's' : ''} added</span>
            <button className="mcf-btn" onClick={openCart} id="menu-view-cart-btn">
              <FaShoppingBag />
              <span>View Cart &amp; Checkout</span>
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default Menu;
